import { computed, ref, type ComputedRef, type Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import type { WorkflowInstance } from '@/types/workflow'
import type {
  FinalAudioOptionsResponse,
  FinalAudioWorkspaceGenerateRequest,
  FinalAudioWorkspaceGenerateResponse,
  FinalAudioWorkspaceMixRequest,
  FinalAudioWorkspaceMixResponse,
} from '@/types/workflow'
import type { WorkflowKnowledgeAudioSelectionItem } from '@/utils/workflowKnowledge'

import { DEFAULT_AUDIO_MIX_CONTROLS } from './finalAudioMixDraft'
import { useFinalAudioStudio } from './useFinalAudioStudio'
import { useFinalDeliveryWorkspace } from './useFinalDeliveryWorkspace'
import { useFinalDeliverySourceControl } from './useFinalDeliverySourceControl'
import { useStoryboardWorkspacePersistence } from './useStoryboardWorkspacePersistence'
import type {
  AudioMixControlDraft,
  FinalAudioNarrationSegmentDraft,
  FinalVideoPlaybackSource,
  StoryboardWorkspaceState,
  UploadedWorkspaceAudio,
  UploadedWorkspaceVideo,
} from './types'

interface UploadedMaterial {
  id?: number
  name?: string
  file_url?: string
  updated_at?: string
}

interface UseFinalStageWorkspaceOptions {
  projectId: number
  routeFullPath: () => string
  routerPush: (location: RouteLocationRaw) => Promise<unknown>
  storyboardWorkspaceInputKey: string
  storyboardWorkspaceState: ComputedRef<StoryboardWorkspaceState>
  workflow: Ref<WorkflowInstance | null>
  sceneVideoFeedbackDrafts: Ref<Record<number, string>>
  finalVideoUploadInput: Ref<HTMLInputElement | null>
  finalBgmUploadInput: Ref<HTMLInputElement | null>
  consumeQueuedKnowledgeAudioSelections: (projectId: number) => WorkflowKnowledgeAudioSelectionItem[]
  updateWorkflowInputData: (projectId: number, payload: { input_data: Record<string, any> }) => Promise<WorkflowInstance>
  uploadMaterial: (formData: FormData, options: { project_id: number; material_type: string; description?: string }) => Promise<UploadedMaterial>
  getFinalAudioOptionsApi: (workflowId: number, params?: { provider?: string }) => Promise<FinalAudioOptionsResponse>
  generateFinalAudioTtsApi: (workflowId: number, params: FinalAudioWorkspaceGenerateRequest) => Promise<FinalAudioWorkspaceGenerateResponse>
  mixFinalAudioIntoVideoApi: (workflowId: number, params: FinalAudioWorkspaceMixRequest) => Promise<FinalAudioWorkspaceMixResponse>
  getActionErrorMessage: (error: any) => string | null
  success: (message: string) => void
  error: (message: string) => void
}

interface UseFinalStageWorkspaceResult {
  audioMixControls: Ref<AudioMixControlDraft>
  audioOptionsLoading: Ref<boolean>
  bgmUploading: Ref<boolean>
  clearGeneratedTts: () => Promise<void>
  clearUploadedBgm: () => Promise<void>
  finalAudioDeliveryVideo: ComputedRef<UploadedWorkspaceVideo | null>
  finalAudioMixing: Ref<boolean>
  finalAudioPreviewVideo: ComputedRef<UploadedWorkspaceVideo | null>
  finalBgmPlan: Ref<string>
  finalTtsProvider: Ref<string>
  finalTtsScript: Ref<string>
  finalTtsStartMs: Ref<number>
  finalTtsSpeed: Ref<number>
  finalTtsVoiceId: Ref<string>
  finalVideoPlayerError: Ref<string>
  finalVideoUploading: Ref<boolean>
  finalVoiceoverDraft: Ref<string>
  finalVoiceoverMode: Ref<string>
  finalVoiceoverTone: Ref<string>
  finalWorkspaceSaving: Ref<boolean>
  finalTtsTimelineSegments: Ref<FinalAudioNarrationSegmentDraft[]>
  getStoryboardWorkspaceStatePayload: () => StoryboardWorkspaceState
  handleApplyPreviewAsDelivery: () => Promise<void>
  handleBgmFileSelected: (event: Event) => Promise<void>
  handleDownloadAsset: (url: string, filename: string) => void
  handleFinalVideoFileSelected: (event: Event) => Promise<void>
  handleFinalVideoLoadedData: () => void
  handleFinalVideoPlaybackError: () => void
  handleGenerateMixPreview: () => Promise<void>
  handleGenerateTts: () => Promise<void>
  handleUseAudioMixDelivery: () => Promise<void>
  handleUseSystemAsDelivery: () => Promise<void>
  handleUseUploadedAsDelivery: () => Promise<void>
  handleOpenAudioLibrary: (role: 'narration' | 'bgm') => void
  handleRemoveTtsSegment: (segmentId: string) => Promise<void>
  handleUpdateTtsSegmentStartMs: (segmentId: string, startMs: number) => Promise<boolean>
  handleSaveFinalWorkspace: () => Promise<void>
  hydrateStoryboardWorkspaceState: () => void
  persistStoryboardWorkspaceState: (nextState: StoryboardWorkspaceState, options?: { successMessage?: string; silent?: boolean }) => Promise<void>
  preferredFinalVideoSource: Ref<FinalVideoPlaybackSource>
  triggerBgmUpload: () => void
  triggerFinalVideoUpload: () => void
  ttsGenerating: Ref<boolean>
  ttsVoiceOptions: Ref<Array<{ label: string; value: string; description?: string | null }>>
  uploadedFinalBgmAudio: Ref<UploadedWorkspaceAudio | null>
}

export function useFinalStageWorkspace(options: UseFinalStageWorkspaceOptions): UseFinalStageWorkspaceResult {
  const finalVideoUploading = ref(false)
  const finalWorkspaceSaving = ref(false)
  const finalVoiceoverMode = ref<string>('keep_current_audio')
  const finalVoiceoverTone = ref<string>('calm')
  const finalBgmPlan = ref<string>('keep_current')
  const finalVoiceoverDraft = ref('')
  const finalTtsScript = ref('')
  const finalTtsStartMs = ref(0)
  const finalTtsVoiceId = ref('')
  const finalTtsProvider = ref('')
  const finalTtsSpeed = ref(1)
  const finalTtsTimelineSegments = ref<FinalAudioNarrationSegmentDraft[]>([])
  const uploadedFinalBgmAudio = ref<UploadedWorkspaceAudio | null>(null)
  const audioMixControls = ref<AudioMixControlDraft>({ ...DEFAULT_AUDIO_MIX_CONTROLS })
  const finalAudioPreviewVideoDraft = ref<UploadedWorkspaceVideo | null>(null)
  const finalAudioDeliveryVideoDraft = ref<UploadedWorkspaceVideo | null>(null)
  const preferredFinalVideoSource = ref<FinalVideoPlaybackSource>('system')

  const {
    getStoryboardWorkspaceStatePayload,
    hydrateStoryboardWorkspaceState,
    persistStoryboardWorkspaceState,
  } = useStoryboardWorkspacePersistence({
    projectId: options.projectId,
    storyboardWorkspaceInputKey: options.storyboardWorkspaceInputKey,
    storyboardWorkspaceState: options.storyboardWorkspaceState,
    workflow: options.workflow,
    sceneVideoFeedbackDrafts: options.sceneVideoFeedbackDrafts,
    finalVoiceoverDraft,
    finalVoiceoverMode,
    finalVoiceoverTone,
    finalBgmPlan,
    finalTtsScript,
    finalTtsStartMs,
    finalTtsVoiceId,
    finalTtsProvider,
    finalTtsSpeed,
    finalTtsTimelineSegments,
    uploadedFinalBgmAudio,
    audioMixControls,
    audioMixPreviewVideo: finalAudioPreviewVideoDraft,
    audioMixDeliveryVideo: finalAudioDeliveryVideoDraft,
    preferredFinalVideoSource,
    updateWorkflowInputData: options.updateWorkflowInputData,
    success: options.success,
  })

  const {
    finalVideoPlayerError,
    handleDownloadAsset,
    handleFinalVideoFileSelected,
    handleFinalVideoLoadedData,
    handleFinalVideoPlaybackError,
    handleSaveFinalWorkspace,
    triggerFinalVideoUpload,
  } = useFinalDeliveryWorkspace({
    projectId: options.projectId,
    finalVideoUploadInput: options.finalVideoUploadInput,
    finalVideoUploading,
    finalWorkspaceSaving,
    getStoryboardWorkspaceStatePayload,
    persistStoryboardWorkspaceState,
    uploadMaterial: options.uploadMaterial,
    getActionErrorMessage: options.getActionErrorMessage,
    error: options.error,
  })

  const finalAudioPreviewVideo = computed(() => finalAudioPreviewVideoDraft.value || options.storyboardWorkspaceState.value.final_delivery?.audio_mix_preview_video || options.storyboardWorkspaceState.value.final_delivery?.mixed_final_video || null)
  const finalAudioDeliveryVideo = computed(() => finalAudioDeliveryVideoDraft.value || options.storyboardWorkspaceState.value.final_delivery?.audio_mix_delivery_video || null)

  const uploadedFinalVideo = computed(() => options.storyboardWorkspaceState.value.final_delivery?.uploaded_final_video || null)
  const systemFinalVideoUrl = computed(() => String(
    options.workflow.value?.data?.video_synthesis_result?.final_video_url
      || options.workflow.value?.data?.video_synthesis_result?.video?.final_video_url
      || options.workflow.value?.data?.final_packaging_result?.deliverables?.video_url
      || options.workflow.value?.data?.video?.file_url
      || ''
  ).trim())

  const {
    audioOptionsLoading,
    bgmUploading,
    clearGeneratedTts,
    clearUploadedBgm,
    finalAudioMixing,
    handleApplyPreviewAsDelivery,
    handleBgmFileSelected,
    handleGenerateMixPreview,
    handleGenerateTts,
    handleOpenAudioLibrary,
    handleRemoveTtsSegment,
    handleUpdateTtsSegmentStartMs,
    triggerBgmUpload,
    ttsGenerating,
    ttsVoiceOptions,
  } = useFinalAudioStudio({
    projectId: options.projectId,
    bgmUploadInput: options.finalBgmUploadInput,
    finalTtsScript,
    finalTtsStartMs,
    finalTtsVoiceId,
    finalTtsProvider,
    finalTtsSpeed,
    finalTtsTimelineSegments,
    uploadedFinalBgmAudio,
    audioMixControls,
    audioMixPreviewVideo: finalAudioPreviewVideoDraft,
    audioMixDeliveryVideo: finalAudioDeliveryVideoDraft,
    preferredFinalVideoSource,
    routeFullPath: options.routeFullPath,
    routerPush: options.routerPush,
    consumeQueuedKnowledgeAudioSelections: options.consumeQueuedKnowledgeAudioSelections,
    getStoryboardWorkspaceStatePayload,
    persistStoryboardWorkspaceState,
    uploadMaterial: options.uploadMaterial,
    getFinalAudioOptionsApi: options.getFinalAudioOptionsApi,
    generateFinalAudioTtsApi: options.generateFinalAudioTtsApi,
    mixFinalAudioIntoVideoApi: options.mixFinalAudioIntoVideoApi,
    success: options.success,
    error: options.error,
  })

  const {
    handleUseAudioMixDelivery,
    handleUseSystemAsDelivery,
    handleUseUploadedAsDelivery,
  } = useFinalDeliverySourceControl({
    systemFinalVideoUrl,
    uploadedFinalVideo,
    audioMixDeliveryVideo: finalAudioDeliveryVideo,
    preferredFinalVideoSource,
    getStoryboardWorkspaceStatePayload,
    persistStoryboardWorkspaceState,
    success: options.success,
    error: options.error,
  })

  return {
    audioMixControls,
    audioOptionsLoading,
    bgmUploading,
    clearGeneratedTts,
    clearUploadedBgm,
    finalAudioDeliveryVideo,
    finalAudioMixing,
    finalAudioPreviewVideo,
    finalBgmPlan,
    finalTtsProvider,
    finalTtsScript,
    finalTtsStartMs,
    finalTtsSpeed,
    finalTtsVoiceId,
    finalVideoPlayerError,
    finalVideoUploading,
    finalVoiceoverDraft,
    finalVoiceoverMode,
    finalVoiceoverTone,
    finalWorkspaceSaving,
    finalTtsTimelineSegments,
    getStoryboardWorkspaceStatePayload,
    handleApplyPreviewAsDelivery,
    handleBgmFileSelected,
    handleDownloadAsset,
    handleFinalVideoFileSelected,
    handleFinalVideoLoadedData,
    handleFinalVideoPlaybackError,
    handleGenerateMixPreview,
    handleGenerateTts,
    handleOpenAudioLibrary,
    handleRemoveTtsSegment,
    handleSaveFinalWorkspace,
    handleUpdateTtsSegmentStartMs,
    handleUseAudioMixDelivery,
    handleUseSystemAsDelivery,
    handleUseUploadedAsDelivery,
    hydrateStoryboardWorkspaceState,
    persistStoryboardWorkspaceState,
    preferredFinalVideoSource,
    triggerBgmUpload,
    triggerFinalVideoUpload,
    ttsGenerating,
    ttsVoiceOptions,
    uploadedFinalBgmAudio,
  }
}
